import React, {useEffect, useRef, useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {Box} from '@material-ui/core';
import {TypographyBox} from 'common/componentsV2/boxTools';
import Button, {COLORS} from 'common/componentsV2/Button';
import Input from 'common/componentsV2/Input';
import * as metricsSelectors from 'metrics/store/selectors';
import {getUserProfile} from 'profile/store/selectors';
import {stringifyQS} from 'common/utils/http';
import {getUniqueId} from 'common/utils/guid';
import {cloneDeep, get, values as lodashValues} from 'lodash';
import deletePropFromObj from 'common/utils/deletePropFromObj';
import {validate} from 'metrics/store/actions';
import {fetchCompositeName, createComposite, resetValidation} from '../../store/actions';
import SelectChipAdvancedValues from './SelectChipAdvancedValues';

type PropTypes = {
  onClose: Function,
  selectedTree: Object,
};

const SaveToCompositeModal = ({onClose, selectedTree}: PropTypes) => {
  const [name, setName] = useState('');
  const [isNameInvalid, setNameInvalid] = useState(false);
  const [isNamePristine, setNamePristine] = useState(true);
  const [title, setTitle] = useState('');
  const [isTitleInvalid, setIsTitleInvalid] = useState(false);
  const [isTitlePristine, setTitlePristine] = useState(true);
  const [isValidationPassed, setIsValidationPassed] = useState(false);
  const [isAdvancedOpen, setAdvancedOpen] = useState(false);
  const [isChipError, setChipError] = useState(false);
  const validationErrorMessage = useRef();
  const additionalPropertiesOptions = useRef();
  const [additionalPropertiesValues, setAdditionalPropertiesValues] = useState([]);
  const dispatch = useDispatch();
  const previewOptions = useSelector(metricsSelectors.getPreviewOptions);
  const [compositeTitleError, setCompositeTitleError] = useState('');
  const compositeNameResponse = useSelector(metricsSelectors.getCompositeNameResponse);
  const compositeNameResponseUpdatedAt = useRef();
  const validationResponse = useSelector(metricsSelectors.getValidationResponse);
  const createCompositeResponse = useSelector(metricsSelectors.getCreateCompositeResponse);
  const user = useSelector(getUserProfile);

  const {expressionTree} = selectedTree;
  compositeNameResponseUpdatedAt.current = 0;
  useEffect(() => {
    setNameInvalid(name === '');
  }, [name]);

  useEffect(() => {
    setIsTitleInvalid(title === '');
  }, [title]);

  // eslint-disable-next-line max-len,no-useless-escape
  const getNotLetterAndNoNumberUtf8Regex = /(?:[\0-\/:-@\[-`\{-\xA9\xAB-\xB1\xB4\xB6-\xB8\xBB\xBF\xD7\xF7\u02C2-\u02C5\u02D2-\u02DF\u02E5-\u02EB\u02ED\u02EF-\u036F\u0375\u0378\u0379\u037E\u0380-\u0385\u0387\u038B\u038D\u03A2\u03F6\u0482-\u0489\u0530\u0557\u0558\u055A-\u055F\u0589-\u05CF\u05EB-\u05EE\u05F3-\u061F\u064B-\u065F\u066A-\u066D\u0670\u06D4\u06D6-\u06E4\u06E7-\u06ED\u06FD\u06FE\u0700-\u070F\u0711\u0730-\u074C\u07A6-\u07B0\u07B2-\u07BF\u07EB-\u07F3\u07F6-\u07F9\u07FB-\u07FF\u0816-\u0819\u081B-\u0823\u0825-\u0827\u0829-\u083F\u0859-\u085F\u086B-\u089F\u08B5\u08C8-\u0903\u093A-\u093C\u093E-\u094F\u0951-\u0957\u0962-\u0965\u0970\u0981-\u0984\u098D\u098E\u0991\u0992\u09A9\u09B1\u09B3-\u09B5\u09BA-\u09BC\u09BE-\u09CD\u09CF-\u09DB\u09DE\u09E2-\u09E5\u09F2\u09F3\u09FA\u09FB\u09FD-\u0A04\u0A0B-\u0A0E\u0A11\u0A12\u0A29\u0A31\u0A34\u0A37\u0A3A-\u0A58\u0A5D\u0A5F-\u0A65\u0A70\u0A71\u0A75-\u0A84\u0A8E\u0A92\u0AA9\u0AB1\u0AB4\u0ABA-\u0ABC\u0ABE-\u0ACF\u0AD1-\u0ADF\u0AE2-\u0AE5\u0AF0-\u0AF8\u0AFA-\u0B04\u0B0D\u0B0E\u0B11\u0B12\u0B29\u0B31\u0B34\u0B3A-\u0B3C\u0B3E-\u0B5B\u0B5E\u0B62-\u0B65\u0B70\u0B78-\u0B82\u0B84\u0B8B-\u0B8D\u0B91\u0B96-\u0B98\u0B9B\u0B9D\u0BA0-\u0BA2\u0BA5-\u0BA7\u0BAB-\u0BAD\u0BBA-\u0BCF\u0BD1-\u0BE5\u0BF3-\u0C04\u0C0D\u0C11\u0C29\u0C3A-\u0C3C\u0C3E-\u0C57\u0C5B-\u0C5F\u0C62-\u0C65\u0C70-\u0C77\u0C7F\u0C81-\u0C84\u0C8D\u0C91\u0CA9\u0CB4\u0CBA-\u0CBC\u0CBE-\u0CDD\u0CDF\u0CE2-\u0CE5\u0CF0\u0CF3-\u0D03\u0D0D\u0D11\u0D3B\u0D3C\u0D3E-\u0D4D\u0D4F-\u0D53\u0D57\u0D62-\u0D65\u0D79\u0D80-\u0D84\u0D97-\u0D99\u0DB2\u0DBC\u0DBE\u0DBF\u0DC7-\u0DE5\u0DF0-\u0E00\u0E31\u0E34-\u0E3F\u0E47-\u0E4F\u0E5A-\u0E80\u0E83\u0E85\u0E8B\u0EA4\u0EA6\u0EB1\u0EB4-\u0EBC\u0EBE\u0EBF\u0EC5\u0EC7-\u0ECF\u0EDA\u0EDB\u0EE0-\u0EFF\u0F01-\u0F1F\u0F34-\u0F3F\u0F48\u0F6D-\u0F87\u0F8D-\u0FFF\u102B-\u103E\u104A-\u104F\u1056-\u1059\u105E-\u1060\u1062-\u1064\u1067-\u106D\u1071-\u1074\u1082-\u108D\u108F\u109A-\u109F\u10C6\u10C8-\u10CC\u10CE\u10CF\u10FB\u1249\u124E\u124F\u1257\u1259\u125E\u125F\u1289\u128E\u128F\u12B1\u12B6\u12B7\u12BF\u12C1\u12C6\u12C7\u12D7\u1311\u1316\u1317\u135B-\u1368\u137D-\u137F\u1390-\u139F\u13F6\u13F7\u13FE-\u1400\u166D\u166E\u1680\u169B-\u169F\u16EB-\u16ED\u16F9-\u16FF\u170D\u1712-\u171F\u1732-\u173F\u1752-\u175F\u176D\u1771-\u177F\u17B4-\u17D6\u17D8-\u17DB\u17DD-\u17DF\u17EA-\u17EF\u17FA-\u180F\u181A-\u181F\u1879-\u187F\u1885\u1886\u18A9\u18AB-\u18AF\u18F6-\u18FF\u191F-\u1945\u196E\u196F\u1975-\u197F\u19AC-\u19AF\u19CA-\u19CF\u19DB-\u19FF\u1A17-\u1A1F\u1A55-\u1A7F\u1A8A-\u1A8F\u1A9A-\u1AA6\u1AA8-\u1B04\u1B34-\u1B44\u1B4C-\u1B4F\u1B5A-\u1B82\u1BA1-\u1BAD\u1BE6-\u1BFF\u1C24-\u1C3F\u1C4A-\u1C4C\u1C7E\u1C7F\u1C89-\u1C8F\u1CBB\u1CBC\u1CC0-\u1CE8\u1CED\u1CF4\u1CF7-\u1CF9\u1CFB-\u1CFF\u1DC0-\u1DFF\u1F16\u1F17\u1F1E\u1F1F\u1F46\u1F47\u1F4E\u1F4F\u1F58\u1F5A\u1F5C\u1F5E\u1F7E\u1F7F\u1FB5\u1FBD\u1FBF-\u1FC1\u1FC5\u1FCD-\u1FCF\u1FD4\u1FD5\u1FDC-\u1FDF\u1FED-\u1FF1\u1FF5\u1FFD-\u206F\u2072\u2073\u207A-\u207E\u208A-\u208F\u209D-\u2101\u2103-\u2106\u2108\u2109\u2114\u2116-\u2118\u211E-\u2123\u2125\u2127\u2129\u212E\u213A\u213B\u2140-\u2144\u214A-\u214D\u214F\u218A-\u245F\u249C-\u24E9\u2500-\u2775\u2794-\u2BFF\u2C2F\u2C5F\u2CE5-\u2CEA\u2CEF-\u2CF1\u2CF4-\u2CFC\u2CFE\u2CFF\u2D26\u2D28-\u2D2C\u2D2E\u2D2F\u2D68-\u2D6E\u2D70-\u2D7F\u2D97-\u2D9F\u2DA7\u2DAF\u2DB7\u2DBF\u2DC7\u2DCF\u2DD7\u2DDF-\u2E2E\u2E30-\u3004\u3008-\u3020\u302A-\u3030\u3036\u3037\u303D-\u3040\u3097-\u309C\u30A0\u30FB\u3100-\u3104\u3130\u318F-\u3191\u3196-\u319F\u31C0-\u31EF\u3200-\u321F\u322A-\u3247\u3250\u3260-\u327F\u328A-\u32B0\u32C0-\u33FF\u4DC0-\u4DFF\u9FFD-\u9FFF\uA48D-\uA4CF\uA4FE\uA4FF\uA60D-\uA60F\uA62C-\uA63F\uA66F-\uA67E\uA69E\uA69F\uA6F0-\uA716\uA720\uA721\uA789\uA78A\uA7C0\uA7C1\uA7CB-\uA7F4\uA802\uA806\uA80B\uA823-\uA82F\uA836-\uA83F\uA874-\uA881\uA8B4-\uA8CF\uA8DA-\uA8F1\uA8F8-\uA8FA\uA8FC\uA8FF\uA926-\uA92F\uA947-\uA95F\uA97D-\uA983\uA9B3-\uA9CE\uA9DA-\uA9DF\uA9E5\uA9FF\uAA29-\uAA3F\uAA43\uAA4C-\uAA4F\uAA5A-\uAA5F\uAA77-\uAA79\uAA7B-\uAA7D\uAAB0\uAAB2-\uAAB4\uAAB7\uAAB8\uAABE\uAABF\uAAC1\uAAC3-\uAADA\uAADE\uAADF\uAAEB-\uAAF1\uAAF5-\uAB00\uAB07\uAB08\uAB0F\uAB10\uAB17-\uAB1F\uAB27\uAB2F\uAB5B\uAB6A-\uAB6F\uABE3-\uABEF\uABFA-\uABFF\uD7A4-\uD7AF\uD7C7-\uD7CA\uD7FC-\uD7FF\uE000-\uF8FF\uFA6E\uFA6F\uFADA-\uFAFF\uFB07-\uFB12\uFB18-\uFB1C\uFB1E\uFB29\uFB37\uFB3D\uFB3F\uFB42\uFB45\uFBB2-\uFBD2\uFD3E-\uFD4F\uFD90\uFD91\uFDC8-\uFDEF\uFDFC-\uFE6F\uFE75\uFEFD-\uFF0F\uFF1A-\uFF20\uFF3B-\uFF40\uFF5B-\uFF65\uFFBF-\uFFC1\uFFC8\uFFC9\uFFD0\uFFD1\uFFD8\uFFD9\uFFDD-\uFFFF]|\uD800[\uDC0C\uDC27\uDC3B\uDC3E\uDC4E\uDC4F\uDC5E-\uDC7F\uDCFB-\uDD06\uDD34-\uDD3F\uDD79-\uDD89\uDD8C-\uDE7F\uDE9D-\uDE9F\uDED1-\uDEE0\uDEFC-\uDEFF\uDF24-\uDF2C\uDF4B-\uDF4F\uDF76-\uDF7F\uDF9E\uDF9F\uDFC4-\uDFC7\uDFD0\uDFD6-\uDFFF]|\uD801[\uDC9E\uDC9F\uDCAA-\uDCAF\uDCD4-\uDCD7\uDCFC-\uDCFF\uDD28-\uDD2F\uDD64-\uDDFF\uDF37-\uDF3F\uDF56-\uDF5F\uDF68-\uDFFF]|\uD802[\uDC06\uDC07\uDC09\uDC36\uDC39-\uDC3B\uDC3D\uDC3E\uDC56\uDC57\uDC77\uDC78\uDC9F-\uDCA6\uDCB0-\uDCDF\uDCF3\uDCF6-\uDCFA\uDD1C-\uDD1F\uDD3A-\uDD7F\uDDB8-\uDDBB\uDDD0\uDDD1\uDE01-\uDE0F\uDE14\uDE18\uDE36-\uDE3F\uDE49-\uDE5F\uDE7F\uDEA0-\uDEBF\uDEC8\uDEE5-\uDEEA\uDEF0-\uDEFF\uDF36-\uDF3F\uDF56\uDF57\uDF73-\uDF77\uDF92-\uDFA8\uDFB0-\uDFFF]|\uD803[\uDC49-\uDC7F\uDCB3-\uDCBF\uDCF3-\uDCF9\uDD24-\uDD2F\uDD3A-\uDE5F\uDE7F\uDEAA-\uDEAF\uDEB2-\uDEFF\uDF28-\uDF2F\uDF46-\uDF50\uDF55-\uDFAF\uDFCC-\uDFDF\uDFF7-\uDFFF]|\uD804[\uDC00-\uDC02\uDC38-\uDC51\uDC70-\uDC82\uDCB0-\uDCCF\uDCE9-\uDCEF\uDCFA-\uDD02\uDD27-\uDD35\uDD40-\uDD43\uDD45\uDD46\uDD48-\uDD4F\uDD73-\uDD75\uDD77-\uDD82\uDDB3-\uDDC0\uDDC5-\uDDCF\uDDDB\uDDDD-\uDDE0\uDDF5-\uDDFF\uDE12\uDE2C-\uDE7F\uDE87\uDE89\uDE8E\uDE9E\uDEA9-\uDEAF\uDEDF-\uDEEF\uDEFA-\uDF04\uDF0D\uDF0E\uDF11\uDF12\uDF29\uDF31\uDF34\uDF3A-\uDF3C\uDF3E-\uDF4F\uDF51-\uDF5C\uDF62-\uDFFF]|\uD805[\uDC35-\uDC46\uDC4B-\uDC4F\uDC5A-\uDC5E\uDC62-\uDC7F\uDCB0-\uDCC3\uDCC6\uDCC8-\uDCCF\uDCDA-\uDD7F\uDDAF-\uDDD7\uDDDC-\uDDFF\uDE30-\uDE43\uDE45-\uDE4F\uDE5A-\uDE7F\uDEAB-\uDEB7\uDEB9-\uDEBF\uDECA-\uDEFF\uDF1B-\uDF2F\uDF3C-\uDFFF]|\uD806[\uDC2C-\uDC9F\uDCF3-\uDCFE\uDD07\uDD08\uDD0A\uDD0B\uDD14\uDD17\uDD30-\uDD3E\uDD40\uDD42-\uDD4F\uDD5A-\uDD9F\uDDA8\uDDA9\uDDD1-\uDDE0\uDDE2\uDDE4-\uDDFF\uDE01-\uDE0A\uDE33-\uDE39\uDE3B-\uDE4F\uDE51-\uDE5B\uDE8A-\uDE9C\uDE9E-\uDEBF\uDEF9-\uDFFF]|\uD807[\uDC09\uDC2F-\uDC3F\uDC41-\uDC4F\uDC6D-\uDC71\uDC90-\uDCFF\uDD07\uDD0A\uDD31-\uDD45\uDD47-\uDD4F\uDD5A-\uDD5F\uDD66\uDD69\uDD8A-\uDD97\uDD99-\uDD9F\uDDAA-\uDEDF\uDEF3-\uDFAF\uDFB1-\uDFBF\uDFD5-\uDFFF]|\uD808[\uDF9A-\uDFFF]|\uD809[\uDC6F-\uDC7F\uDD44-\uDFFF]|[\uD80A\uD80B\uD80E-\uD810\uD812-\uD819\uD824-\uD82B\uD82D\uD82E\uD830-\uD833\uD836\uD837\uD839\uD83D\uD83F\uD87B-\uD87D\uD87F\uD885-\uDBFF][\uDC00-\uDFFF]|\uD80D[\uDC2F-\uDFFF]|\uD811[\uDE47-\uDFFF]|\uD81A[\uDE39-\uDE3F\uDE5F\uDE6A-\uDECF\uDEEE-\uDEFF\uDF30-\uDF3F\uDF44-\uDF4F\uDF5A\uDF62\uDF78-\uDF7C\uDF90-\uDFFF]|\uD81B[\uDC00-\uDE3F\uDE97-\uDEFF\uDF4B-\uDF4F\uDF51-\uDF92\uDFA0-\uDFDF\uDFE2\uDFE4-\uDFFF]|\uD821[\uDFF8-\uDFFF]|\uD823[\uDCD6-\uDCFF\uDD09-\uDFFF]|\uD82C[\uDD1F-\uDD4F\uDD53-\uDD63\uDD68-\uDD6F\uDEFC-\uDFFF]|\uD82F[\uDC6B-\uDC6F\uDC7D-\uDC7F\uDC89-\uDC8F\uDC9A-\uDFFF]|\uD834[\uDC00-\uDEDF\uDEF4-\uDF5F\uDF79-\uDFFF]|\uD835[\uDC55\uDC9D\uDCA0\uDCA1\uDCA3\uDCA4\uDCA7\uDCA8\uDCAD\uDCBA\uDCBC\uDCC4\uDD06\uDD0B\uDD0C\uDD15\uDD1D\uDD3A\uDD3F\uDD45\uDD47-\uDD49\uDD51\uDEA6\uDEA7\uDEC1\uDEDB\uDEFB\uDF15\uDF35\uDF4F\uDF6F\uDF89\uDFA9\uDFC3\uDFCC\uDFCD]|[\uD838\uD83C][\uDC00-\uDCFF\uDD2D-\uDD36\uDD3E\uDD3F\uDD4A-\uDD4D\uDD4F-\uDEBF\uDEEC-\uDEEF\uDEFA-\uDFFF]|\uD83A[\uDCC5\uDCC6\uDCD0-\uDCFF\uDD44-\uDD4A\uDD4C-\uDD4F\uDD5A-\uDFFF]|\uD83B[\uDC00-\uDC70\uDCAC\uDCB0\uDCB5-\uDD00\uDD2E\uDD3E-\uDDFF\uDE04\uDE20\uDE23\uDE25\uDE26\uDE28\uDE33\uDE38\uDE3A\uDE3C-\uDE41\uDE43-\uDE46\uDE48\uDE4A\uDE4C\uDE50\uDE53\uDE55\uDE56\uDE58\uDE5A\uDE5C\uDE5E\uDE60\uDE63\uDE65\uDE66\uDE6B\uDE73\uDE78\uDE7D\uDE7F\uDE8A\uDE9C-\uDEA0\uDEA4\uDEAA\uDEBC-\uDFFF]|\uD83E[\uDC00-\uDFEF\uDFFA-\uDFFF]|\uD869[\uDEDE-\uDEFF]|\uD86D[\uDF35-\uDF3F]|\uD86E[\uDC1E\uDC1F]|\uD873[\uDEA2-\uDEAF]|\uD87A[\uDFE1-\uDFFF]|\uD87E[\uDE1E-\uDFFF]|\uD884[\uDF4B-\uDFFF]|[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?:[^\uD800-\uDBFF]|^)[\uDC00-\uDFFF])+/g;
  const cleanPropertyText = (text) => text.replace(getNotLetterAndNoNumberUtf8Regex, '_');

  const onNameChange = (val) => {
    setName(cleanPropertyText(val));
    setNamePristine(false);
  };

  const onTitleChange = (val) => {
    setTitle(val);
    setTitlePristine(false);
    if (isNamePristine) {
      setName(cleanPropertyText(val));
    }
  };

  useEffect(() => {
    if (!createCompositeResponse.isLoading && createCompositeResponse.data) {
      if (createCompositeResponse.data.validationResults.passed) {
        dispatch(resetValidation());
        onClose();
      }
      const errorMessage = get(
        lodashValues(get(createCompositeResponse.data, 'validationResults.failures')),
        '[0][0].message',
        '',
      );
      setCompositeTitleError(errorMessage);
    }
  }, [createCompositeResponse.isLoading]);

  useEffect(() => {
    setIsValidationPassed(validationResponse.passed);
    const errorMessage = get(lodashValues(get(validationResponse, 'failures')), '[0][0].message', '');
    validationErrorMessage.current = errorMessage;
  }, [validationResponse.meta]);

  useEffect(() => {
    if (!compositeNameResponse.isLoading && compositeNameResponse.data) {
      if (!compositeNameResponse.data.validation.passed) {
        return;
      }
      const {nameComposition} = compositeNameResponse.data;
      additionalPropertiesOptions.current = (nameComposition.optional || []).map((prop) => ({
        label: `${prop.key}=${prop.value}`,
        value: prop.value,
      }));
      setAdditionalPropertiesValues(
        (nameComposition.suggested || []).map((prop) => ({label: `${prop.key}=${prop.value}`, value: prop.value})),
      );
      if (nameComposition.what) {
        setNamePristine(false);
        setName(nameComposition.what);
      }
      if (nameComposition.what === '') {
        setNamePristine(false);
        setName('');
      }
    }
  }, [compositeNameResponse.isLoading]);

  const isEmptyFunction =
    expressionTree.root &&
    expressionTree.root.function === '' &&
    expressionTree.root.children.length === 1 &&
    expressionTree.root.children[0].type === 'metric';

  const removeUIData = (tree) => {
    const treeCleaned = cloneDeep(tree);
    deletePropFromObj(treeCleaned, 'uiData');
    deletePropFromObj(treeCleaned, 'q');
    deletePropFromObj(treeCleaned, 'ids');
    return treeCleaned;
  };
  const requestCompositeName = () => {
    const fromDate = Math.ceil(previewOptions.dateRange.startDate / 1000);
    const toDate = Math.ceil(previewOptions.dateRange.endDate / 1000);
    dispatch(
      fetchCompositeName({
        urlExt: stringifyQS({
          fromDate,
          toDate,
          index: 0,
          size: 5,
          includeSuggestions: true,
          maxDataPoints: 500,
          resolution: previewOptions.timeScale,
        }),
        body: {
          compositeCount: 0,
          displayOnly: false,
          expressionTree: removeUIData(isEmptyFunction ? {root: expressionTree.root.children[0]} : expressionTree),
          id: getUniqueId(),
          name: {
            prefix: '',
            auto: true,
          },
          uiIndex: 0,
          namingSchema: 'COMPOSITE_V2',
          excludeComposites: true,
          mtype: 'COMPOSITE',
        },
      }),
    );
  };

  useEffect(() => {
    requestCompositeName();
    const validationObject = {
      compositeCount: 0,
      displayOnly: false,
      expressionTree: removeUIData(expressionTree),
      filter: {
        function: 'alphanumeric',
        parameters: [
          {
            name: 'Top N',
            value: 10,
          },
        ],
        children: [],
        id: getUniqueId(),
        type: 'function',
      },
      id: getUniqueId(),
      name: {
        prefix: '',
        auto: false,
      },
      scalarTransforms: null,
      uiIndex: 0,
      namingSchema: 'COMPOSITE_V2',
      excludeComposites: true,
      mtype: 'COMPOSITE',
      title: '',
    };
    dispatch(validate({body: validationObject, url: '?shouldValidateTitle=false'}, 'composite'));
  }, []);

  const onSaveClick = () => {
    const prefix = `what=${name}.${additionalPropertiesValues.map((val) => val.label).join('.')}`;

    const compositeObject = {
      compositeCount: 0,
      displayOnly: false,
      expressionTree: removeUIData(expressionTree),
      filter: {
        function: 'alphanumeric',
        parameters: [
          {
            name: 'Top N',
            value: 10,
          },
        ],
        children: [],
        id: getUniqueId(),
        type: 'function',
      },
      name: {
        prefix,
        auto: false,
      },
      scalarTransforms: null,
      uiIndex: 0,
      namingSchema: 'COMPOSITE_V2',
      excludeComposites: true,
      mtype: 'COMPOSITE',
      title,
      owner: user.email,
    };
    dispatch(createComposite(compositeObject));
  };

  const onAdditionalPropertiesChange = (val) => {
    setAdditionalPropertiesValues(val);
  };

  if (!isValidationPassed) {
    return (
      <Box>
        <TypographyBox mb={4} fontWeight={300} fontSize={24}>
          Cannot create a composite
        </TypographyBox>
        <Box color="gray.300">{validationErrorMessage.current}</Box>
        <Box mt={12} display="flex" justifyContent="flex-end">
          <Button text="Cancel" colorSchema={COLORS.GRAY_300} onClick={onClose} />
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <div>
        <TypographyBox mb={4} fontWeight={300} fontSize={24} automation-id="metricExplorerCompositeTitle">
          Save to Composite
        </TypographyBox>
        <div>Title</div>
        <Input
          placeHolder="Set a unique composite title"
          value={title}
          isInvalid={(isTitleInvalid && !isTitlePristine) || compositeTitleError !== ''}
          onChange={(event) => onTitleChange(event.target.value)}
        />
        {compositeTitleError !== '' && (
          <Box mt={0.5} color="red.500" fontSize={14} fontWeight="bold">
            {compositeTitleError}
          </Box>
        )}
        <Box
          mt={1}
          mb={1}
          css={{cursor: 'pointer'}}
          color="blue.500"
          onClick={() => setAdvancedOpen(!isAdvancedOpen)}
          display="flex"
          alignItems="center"
          automation-id="metricExplorerCompositeAdvancedOptions"
        >
          <div>Advanced options</div>
          <div className={['icon', isAdvancedOpen ? 'icn-arrow16-triangleup' : 'icn-arrow16-triangledown'].join(' ')} />
        </Box>
        {isAdvancedOpen && (
          <div>
            <Box mt={2} automation-id="metricExplorerCompositeMeasure">
              <div>What are you measuring (what=)</div>
              <Input value={name} isInvalid={isNameInvalid} onChange={(event) => onNameChange(event.target.value)} />
            </Box>
            <Box mt={2}>
              <div>Additional Properties</div>
              {isChipError && (
                <Box color="red.500" fontSize="12">
                  Input error. Value format must be KEY=VALUE
                </Box>
              )}
              <SelectChipAdvancedValues
                value={additionalPropertiesValues}
                options={additionalPropertiesOptions.current || []}
                onChange={(values) => onAdditionalPropertiesChange(values)}
                onError={(val) => setChipError(val)}
                onRemove={(removed) => {
                  onAdditionalPropertiesChange(
                    additionalPropertiesValues.filter((item) => item.value !== removed.value),
                  );
                }}
              />
            </Box>
          </div>
        )}
      </div>
      <Box mt={12} display="flex" justifyContent="flex-end">
        <Box mr={1}>
          <Button text="Cancel" colorSchema={COLORS.GRAY_300} onClick={onClose} />
        </Box>
        <Button
          text="Save"
          isDisabled={title === '' || name === ''}
          colorSchema={COLORS.BLUE_500}
          onClick={onSaveClick}
          automationId="metricExplorerCompositeSave"
        />
      </Box>
    </Box>
  );
};

export default SaveToCompositeModal;
